import React from 'react'
import { Card } from '@/components/ui'
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts'
import { FactorAnalysisResult, FactorKey, StudioSubscriptionType } from '@/types'
import { formatCurrency, getCurrencySymbol } from '@/lib/utils'
import { useChartPalette } from '@/hooks/useThemeColors'
import type { RecurringSalesDetailItem, RecurringSalesDetailMode } from '@/hooks/useSalesMixAnalytics'

// ---------- helpers ----------
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v))

const getConversionRate = (current: number, previous: number) => {
  if (!previous) return 0
  return Math.round((current / previous) * 100)
}

const getDeviation = (fact: number, plan: number) => {
  if (!plan) return 0
  return Math.round(((fact - plan) / plan) * 100)
}

const fmtInt = (n: number) => {
  if (!Number.isFinite(n)) return '0'
  return Math.round(n).toLocaleString('ru-RU')
}

const formatSignedPercent = (value: number) => {
  const rounded = Math.round(value)
  return `${rounded >= 0 ? '+' : ''}${rounded}%`
}

type XY = { date: string; value: number }
type WaterfallMode = 'plan_fact' | 'plan_forecast' | 'plan_growth'

interface WaterfallStep {
  key: string
  label: string
  kind: 'total' | 'delta'
  value: number
  start: number
  end: number
}

const WATERFALL_FACTORS: FactorKey[] = ['leads', 'cr1', 'cr2', 'cr3', 'aov']

const WATERFALL_FACTOR_LABELS: Record<FactorKey, string> = {
  leads: 'Заявки',
  cr1: 'Конв. в запись',
  cr2: 'Доходимость',
  cr3: 'Конв. в продажу',
  aov: 'Средний чек'
}

const WATERFALL_MODE_LABELS: Record<WaterfallMode, string> = {
  plan_fact: 'План - Факт',
  plan_forecast: 'План - Текущий прогноз',
  plan_growth: 'План - Потенциал роста'
}

const WATERFALL_MODE_DESCRIPTIONS: Record<WaterfallMode, string> = {
  plan_fact:
    'Показывает, как каждый фактор уже повлиял на отклонение выручки: от плана на сегодня до факта на сегодня (цепная декомпозиция).',
  plan_forecast:
    'Показывает отклонение к концу периода, если ничего не менять: факт выручки на сегодня + прогноз остатка по средним показателям за последние 10 дней.',
  plan_growth:
    'Показывает отклонение к концу периода с учетом управляемого потенциала: догоняем до плана отстающие метрики, а превышения фиксируем на текущем уровне.'
}

const WATERFALL_MODE_INSTRUCTIONS: Record<WaterfallMode, { useCase: string; reading: string }> = {
  plan_fact: {
    useCase: 'Для ежедневного контроля: где уже потеряли/перевыполнили выручку к текущей дате.',
    reading: 'Смотрите красные/зеленые шаги между "План" и "Факт": это фактическое влияние каждого фактора на сегодня.'
  },
  plan_forecast: {
    useCase: 'Главный режим для управленческих действий до конца периода.',
    reading: 'Показывает разрыв "План -> Прогноз" с учетом текущего темпа. Этот же разрыв детализирован в блоке "Что нужно делать в первую очередь".'
  },
  plan_growth: {
    useCase: 'Для сценария "что будет, если дотянуть отстающие метрики до плана".',
    reading: 'Показывает максимально достижимый результат без роста сверх плана по факторам, где вы уже выше плана.'
  }
}

function getPotentialToPeriodEnd(row: FactorAnalysisResult['factors'][number] | undefined): number {
  if (!row || row.planValue === null || row.currentValue === row.planValue) return 0
  return Math.max(0, row.expectedContributionByPlanCap || 0)
}

function getNumber(value: number | null | undefined): number {
  return Number.isFinite(value as number) ? (value as number) : 0
}

function getRemainingForecastDeltas(data: FactorAnalysisResult): number[] {
  const rowsByFactor = new Map(data.factors.map((row) => [row.factor, row]))
  const daysLeft = Math.max(0, data.daysLeftInPeriod || 0)

  const leadsPlanPerWeek = getNumber(rowsByFactor.get('leads')?.planValue)
  const leadsPlanRemaining = (leadsPlanPerWeek / 7) * daysLeft
  const leadsCurrentRemaining = Math.max(0, getNumber(data.base.leadsPerDay) * daysLeft)

  const c1p = getNumber(rowsByFactor.get('cr1')?.planValue)
  const c2p = getNumber(rowsByFactor.get('cr2')?.planValue)
  const c3p = getNumber(rowsByFactor.get('cr3')?.planValue)
  const ap = getNumber(rowsByFactor.get('aov')?.planValue)

  const c1f = getNumber(data.base.cr1)
  const c2f = getNumber(data.base.cr2)
  const c3f = getNumber(data.base.cr3)
  const af = getNumber(data.base.aov)

  const planRem = leadsPlanRemaining * c1p * c2p * c3p * ap
  const step1 = leadsCurrentRemaining * c1p * c2p * c3p * ap
  const step2 = leadsCurrentRemaining * c1f * c2p * c3p * ap
  const step3 = leadsCurrentRemaining * c1f * c2f * c3p * ap
  const step4 = leadsCurrentRemaining * c1f * c2f * c3f * ap
  const remForecast = leadsCurrentRemaining * c1f * c2f * c3f * af

  return [
    step1 - planRem,
    step2 - step1,
    step3 - step2,
    step4 - step3,
    remForecast - step4
  ]
}

function buildWaterfallForMode(data: FactorAnalysisResult, mode: WaterfallMode): WaterfallStep[] {
  const rowsByFactor = new Map(data.factors.map((row) => [row.factor, row]))
  const factDeltas = WATERFALL_FACTORS.map((factor) => getNumber(rowsByFactor.get(factor)?.lostToDateAmount))
  const remainingForecastDeltas = getRemainingForecastDeltas(data)

  const planFactStart = Math.max(0, getNumber(data.planPeriodToDateNewRevenue))
  const planForecastStart = Math.max(0, getNumber(data.planPeriodNewRevenue))

  const rawDeltas = WATERFALL_FACTORS.map((factor, idx) => {
    const row = rowsByFactor.get(factor)
    if (mode === 'plan_fact') return factDeltas[idx] || 0
    if (mode === 'plan_forecast') return (factDeltas[idx] || 0) + (remainingForecastDeltas[idx] || 0)
    if (mode === 'plan_growth') {
      return (factDeltas[idx] || 0) + (remainingForecastDeltas[idx] || 0) + getPotentialToPeriodEnd(row)
    }
    return 0
  })

  const totalPotential = WATERFALL_FACTORS.reduce(
    (sum, factor) => sum + getPotentialToPeriodEnd(rowsByFactor.get(factor)),
    0
  )

  const plan = mode === 'plan_fact' ? planFactStart : planForecastStart
  const targetEnd = mode === 'plan_fact'
    ? Math.max(0, data.factPeriodToDateNewRevenue || 0)
    : mode === 'plan_growth'
    ? data.forecastPeriodEndNewRevenue + totalPotential
    : data.forecastPeriodEndNewRevenue

  const endLabel = mode === 'plan_fact'
    ? 'Факт'
    : mode === 'plan_growth'
    ? 'Рост'
    : 'Прогноз'

  const steps: WaterfallStep[] = [
    {
      key: 'plan',
      label: 'План',
      kind: 'total',
      value: plan,
      start: 0,
      end: plan
    }
  ]

  let running = plan
  WATERFALL_FACTORS.forEach((factor, index) => {
    const delta = rawDeltas[index] || 0
    const next = running + delta
    steps.push({
      key: factor,
      label: WATERFALL_FACTOR_LABELS[factor],
      kind: 'delta',
      value: delta,
      start: running,
      end: next
    })
    running = next
  })

  steps.push({
    key: 'end',
    label: endLabel,
    kind: 'total',
    value: targetEnd,
    start: 0,
    end: targetEnd
  })

  return steps
}

function computeFunnelWidths(values: number[], minPct = 48, maxPct = 100) {
  // Линейное уменьшение ширины для прямых краев воронки
  const steps = values.length - 1
  const stepSize = (maxPct - minPct) / steps
  
  return values.map((_, i) => maxPct - stepSize * i)
}

// ---------- SVG funnel segment ----------
interface FunnelSegmentProps {
  idBase: string
  color: string
  topWidthPct: number
  bottomWidthPct: number
  daily: XY[]
  plan: number
  heightPx?: number
  children?: React.ReactNode
}

const FunnelSegment: React.FC<FunnelSegmentProps> = ({
  idBase,
  color,
  topWidthPct,
  bottomWidthPct,
  daily,
  plan,
  heightPx = 74,
  children
}) => {
  const leftBleed = 'clamp(108px, 24vw, 168px)'
  const topInset = (100 - topWidthPct) / 2
  const bottomInset = (100 - bottomWidthPct) / 2
  const points = `${topInset},0 ${100 - topInset},0 ${100 - bottomInset},100 ${bottomInset},100`

  const clipId = `${idBase}-clip`
  const gradId = `${idBase}-grad`
  const shadowId = `${idBase}-shadow`

  const maxDaily = Math.max(...daily.map((d) => d.value), 1)
  const days = daily.length
  const planPerDay = days > 0 ? plan / days : 0

  // область “микрогиста” по Y (чтобы не лез на текст слишком сильно)
  const chartTop = 42
  const chartBottom = 98
  const chartH = chartBottom - chartTop

  const planLineY = chartBottom - clamp(planPerDay / maxDaily, 0, 1) * chartH

  return (
    <div style={{ position: 'relative', width: '100%', height: heightPx }}>
      <svg
        viewBox="0 0 100 100"
        width={`calc(100% + ${leftBleed})`}
        height="100%"
        preserveAspectRatio="none"
        style={{ display: 'block', position: 'absolute', left: `calc(-1 * ${leftBleed})`, top: 0 }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.65} />
            <stop offset="100%" stopColor={color} stopOpacity={0.50} />
          </linearGradient>

          <clipPath id={clipId}>
            <polygon points={points} />
          </clipPath>

          <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2.2" stdDeviation="2.4" floodOpacity="0.15" />
          </filter>
        </defs>

        {/* base shape */}
        <g filter={`url(#${shadowId})`}>
          <polygon
            points={points}
            fill={`url(#${gradId})`}
            stroke="rgba(255,255,255,0.45)"
            strokeWidth="0.8"
          />
        </g>

        {/* micro histogram (clipped) — subtle bottom band + fixed slots */}
        <g clipPath={`url(#${clipId})`} opacity={0.12}>
          {(() => {
            const days = daily.length
            if (!days) return null

            // Рисуем в нижней полосе (чтобы не спорило с текстом)
            const bandTop = 76
            const bandBottom = 98
            const bandH = bandBottom - bandTop

            // Узкая центральная зона для спарклайна (~2см визуально)
            const chartStart = 42
            const chartEnd = 58
            const chartWidth = chartEnd - chartStart

            // Рисуем ровно столько столбиков, сколько есть дней
            const slots = days
            const bw = chartWidth / slots

            // Фиксированный зазор между столбиками = 15% от ширины столбика
            const gapRatio = 0.15
            const barWidthRatio = 1 / (1 + gapRatio) // ≈ 0.87
            const barWidth = bw * barWidthRatio
            const gap = bw - barWidth

            const maxDaily = Math.max(...daily.map((d) => d.value), 1)

            const getSlotValue = (slotIndex: number) => {
              // Берем значение дня напрямую по индексу
              return daily[slotIndex]?.value ?? 0
            }

            // линия "план/день" в той же полосе (только в центральной зоне)
            const planPerDay = plan > 0 ? plan / days : 0
            const planLineY = bandBottom - clamp(planPerDay / maxDaily, 0, 1) * bandH

            return (
              <>
                {Array.from({ length: slots }).map((_, i) => {
                  const v = getSlotValue(i)
                  const h = clamp(v / maxDaily, 0, 1) * bandH
                  const y = bandBottom - h

                  return (
                    <rect
                      key={i}
                      x={chartStart + i * bw}
                      y={y}
                      width={barWidth}
                      height={h}
                      fill="rgba(0,0,0,0.55)"
                      rx={0.35}
                    />
                  )
                })}

                {plan > 0 && (
                  <line
                    x1={chartStart}
                    x2={chartEnd}
                    y1={planLineY}
                    y2={planLineY}
                    stroke="rgba(255,255,255,0.85)"
                    strokeWidth={0.9}
                    strokeDasharray="2.2 2.2"
                  />
                )}
              </>
            )
          })()}
        </g>

        {/* subtle highlight */}
        <g clipPath={`url(#${clipId})`} opacity={0.12}>
          <rect x={0} y={0} width={100} height={30} fill="rgba(255,255,255,1)" />
        </g>
      </svg>

      {/* overlay content */}
      <div
        className="absolute inset-0 flex items-center justify-between px-2 py-2 sm:px-3 sm:py-2 gap-2 sm:gap-3"
      >
        {children}
      </div>
    </div>
  )
}

// ---------- row ----------
interface FunnelRowProps {
  idBase: string
  label: string
  fact: number
  plan: number
  planToDate?: number
  nextConversion?: number
  nextConversionPlan?: number
  showConversionHeader?: boolean
  topWidthPct: number
  bottomWidthPct: number
  color: string
  daily: XY[]
  trend: XY[]
}

const FunnelRow: React.FC<FunnelRowProps> = ({
  idBase,
  label,
  fact,
  plan,
  planToDate,
  nextConversion,
  nextConversionPlan,
  showConversionHeader,
  topWidthPct,
  bottomWidthPct,
  color,
  daily,
  trend
}) => {
  // Отклонение печатается инлайн-стилем (цвет уходит в атрибут, не в класс),
  // поэтому оттенок берётся из темы значением. Лестница и оттенок те же, что
  // были: green-600 / red-600 — в светлой теме цвет не меняется, в тёмной
  // приходит зеркало (green-400 / red-400).
  const palette = useChartPalette()
  const deviation = getDeviation(fact, planToDate ?? plan)
  const planRatio = plan > 0 ? fact / plan : 0
  const planPct = plan > 0 ? Math.round(planRatio * 100) : 0

  const devColor = deviation >= 0 ? palette('green', 600, '#16a34a') : palette('red', 600, '#dc2626')
  const roundedNextConversionPlan = nextConversionPlan !== undefined && nextConversionPlan !== null
    ? Math.round(nextConversionPlan)
    : 0
  const convDev = nextConversionPlan !== undefined && nextConversionPlan !== null
    ? (nextConversion || 0) - roundedNextConversionPlan
    : 0
  const convDevColor = convDev >= 0 ? palette('green', 600, '#16a34a') : palette('red', 600, '#dc2626')
  const hasConversion = nextConversion !== undefined && nextConversion !== null

  return (
    <div
      className="relative isolate grid grid-cols-[100px_1fr] sm:grid-cols-[120px_1fr] lg:grid-cols-[150px_1fr] gap-2 sm:gap-3 items-center py-2"
    >
      {/* label */}
      <div className="relative z-20 flex flex-col gap-1">
        <div className="text-sm sm:text-base font-extrabold text-gray-900">{label}</div>
        <div className="text-[10px] sm:text-xs text-gray-600">
          {plan > 0 ? `Выполнение: ${planPct}%` : 'План не задан'}
        </div>
      </div>

      {/* funnel segment */}
      <div className="w-full">
        <FunnelSegment
          idBase={idBase}
          color={color}
          topWidthPct={topWidthPct}
          bottomWidthPct={bottomWidthPct}
          daily={daily}
          plan={plan}
        >
          {/* LEFT metrics */}
          <div className="flex flex-col gap-1 min-w-[80px] sm:min-w-[120px] lg:min-w-[160px]">
            <div className="text-xs sm:text-sm lg:text-base font-extrabold text-gray-900 leading-tight">
              {fmtInt(fact)}
              <span className="font-semibold opacity-75"> / {fmtInt(plan)}</span>
            </div>

            <div className="text-[10px] sm:text-xs font-extrabold" style={{ color: devColor }}>
              {deviation >= 0 ? '+' : ''}
              {deviation}%
            </div>
          </div>

          {/* RIGHT conversion or header */}
          <div className="flex flex-col items-end gap-0.5 sm:gap-1">
            {showConversionHeader ? (
              <div className="text-xs sm:text-sm font-semibold text-gray-900">
                Конверсии
              </div>
            ) : hasConversion ? (
              <>
                <div className="text-sm sm:text-base lg:text-lg font-black text-gray-900 leading-tight">
                  {nextConversion}%
                </div>
                <div className="text-[9px] sm:text-[10px] text-gray-700">
                  План: {roundedNextConversionPlan}%
                </div>
                <div className="text-[9px] sm:text-[10px] font-extrabold" style={{ color: convDevColor }}>
                  {nextConversionPlan !== undefined && nextConversionPlan !== null
                    ? `${convDev >= 0 ? '+' : ''}${convDev} п.п.`
                    : '—'}
                </div>
              </>
            ) : null}
          </div>
        </FunnelSegment>
      </div>
    </div>
  )
}

interface RecurringSalesDetailsCardProps {
  subscriptionType: StudioSubscriptionType
  details: Record<RecurringSalesDetailMode, RecurringSalesDetailItem[]>
  mode: RecurringSalesDetailMode
  onModeChange: (mode: RecurringSalesDetailMode) => void
  currency: string
}

const RecurringSalesDetailsCard: React.FC<RecurringSalesDetailsCardProps> = ({
  subscriptionType,
  details,
  mode,
  onModeChange,
  currency
}) => {
  const activeMode: RecurringSalesDetailMode = subscriptionType === 'SUBSCRIPTION'
    ? 'subscription'
    : subscriptionType === 'REGULAR'
    ? 'regular'
    : mode
  const items = details[activeMode] || []
  const showModeSwitch = subscriptionType === 'MIXED'

  return (
    <Card className="h-full p-3.5">
      <div className="mb-2.5">
        <h3 className="text-base sm:text-lg font-medium leading-tight">Детализация постоянных</h3>
        {showModeSwitch && (
          <div className="mt-2 inline-flex w-full rounded-lg bg-muted p-0.5 shrink-0">
            {([
              { key: 'regular', label: 'Абонементы' },
              { key: 'subscription', label: 'Подписки' }
            ] as const).map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => onModeChange(item.key)}
                className={`flex-1 px-2.5 py-1 text-[11px] rounded-md transition-colors text-center whitespace-nowrap ${
                  activeMode === item.key
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2.5">
        {items.map((item) => {
          const isPositive = item.revenueDeviationPct >= 0

          return (
            <div key={item.key} className="rounded-lg border border-border bg-card p-3 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold leading-snug text-gray-900 pr-1">{item.title}</p>
                <span className={`text-sm font-bold leading-none whitespace-nowrap ${
                  isPositive ? 'text-green-700' : 'text-red-700'
                }`}>
                  {formatSignedPercent(item.revenueDeviationPct)}
                </span>
              </div>

              <div className="mt-2 text-base font-semibold leading-snug text-gray-900 break-words">
                {formatCurrency(item.factRevenue, currency)} / {formatCurrency(item.planRevenue, currency)}
              </div>
              <div className="mt-1.5 grid gap-1 text-xs leading-tight text-muted-foreground">
                <div>
                  Кол-во: <span className="font-medium text-foreground">{fmtInt(item.factCount)} / {fmtInt(item.planCount)}</span>
                </div>
                <div>
                  Ср. чек: <span className="font-medium text-foreground">{formatCurrency(item.factAvgCheck, currency)} / {formatCurrency(item.planAvgCheck, currency)}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

// ---------- main ----------
interface SalesFunnelProps {
  // fact
  leads: number
  appointments: number
  visits: number
  sales: number

  // plan
  leadsplan?: number
  appointmentsPlan?: number
  visitsPlan?: number
  salesPlan?: number

  // conversion plans
  bookingConversionPlan?: number
  visitConversionPlan?: number
  salesConversionPlan?: number

  // daily breakdown
  dailyBreakdown?: Array<{
    date: string
    leads: number
    appointments: number
    visits: number
    sales: number
  }>

  // trend data
  trendData?: Array<{
    date: string
    leads: number
    appointments: number
    visits: number
    sales: number
  }>

  filterValue?: string
  onFilterChange?: (value: string) => void
  availableWeeks?: Array<{ value: string; label: string }>
  // Теоретические границы периода
  periodStartDate?: string
  periodEndDate?: string
  factorAnalysisData?: FactorAnalysisResult | null
  factorAnalysisVariants?: {
    mixed?: FactorAnalysisResult | null
    regular?: FactorAnalysisResult | null
    subscription?: FactorAnalysisResult | null
  }
  allowFactorSplitViews?: boolean
  subscriptionType?: StudioSubscriptionType
  recurringSalesDetails?: Record<RecurringSalesDetailMode, RecurringSalesDetailItem[]>
  todayDate?: string
  currency?: string
}

export function SalesFunnel({
  leads,
  appointments,
  visits,
  sales,
  leadsplan = 0,
  appointmentsPlan = 0,
  visitsPlan = 0,
  salesPlan = 0,
  bookingConversionPlan = 0,
  visitConversionPlan = 0,
  salesConversionPlan = 0,
  dailyBreakdown = [],
  trendData = [],
  filterValue = 'current_period',
  onFilterChange,
  availableWeeks = [],
  periodStartDate,
  periodEndDate,
  factorAnalysisData = null,
  factorAnalysisVariants,
  allowFactorSplitViews = false,
  subscriptionType = 'REGULAR',
  recurringSalesDetails = { regular: [], subscription: [] },
  todayDate,
  currency = 'RUB'
}: SalesFunnelProps) {
  const currencySymbol = getCurrencySymbol(currency)
  const [waterfallMode, setWaterfallMode] = React.useState<WaterfallMode>('plan_forecast')
  const [factorViewMode, setFactorViewMode] = React.useState<'mixed' | 'regular' | 'subscription'>('mixed')
  const [recurringDetailMode, setRecurringDetailMode] = React.useState<RecurringSalesDetailMode>('regular')
  const [isWaterfallGuideOpen, setIsWaterfallGuideOpen] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem('analytics_waterfall_guide_open') === '1'
  })
  const [isFactorGuideOpen, setIsFactorGuideOpen] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem('analytics_factor_guide_open') === '1'
  })
  const hasData = leads > 0 || appointments > 0 || visits > 0 || sales > 0

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem('analytics_waterfall_guide_open', isWaterfallGuideOpen ? '1' : '0')
  }, [isWaterfallGuideOpen])

  React.useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem('analytics_factor_guide_open', isFactorGuideOpen ? '1' : '0')
  }, [isFactorGuideOpen])

  // daily micro hist
  const daily = {
    leads: dailyBreakdown.map((d) => ({ date: d.date, value: d.leads })),
    appointments: dailyBreakdown.map((d) => ({ date: d.date, value: d.appointments })),
    visits: dailyBreakdown.map((d) => ({ date: d.date, value: d.visits })),
    sales: dailyBreakdown.map((d) => ({ date: d.date, value: d.sales }))
  }

  // trend sparklines (fallback to dailyBreakdown if trendData empty)
  const srcTrend = trendData.length ? trendData : dailyBreakdown
  const trend = {
    leads: srcTrend.map((d) => ({ date: d.date, value: d.leads })),
    appointments: srcTrend.map((d) => ({ date: d.date, value: d.appointments })),
    visits: srcTrend.map((d) => ({ date: d.date, value: d.visits })),
    sales: srcTrend.map((d) => ({ date: d.date, value: d.sales }))
  }

  // conversions to next stage
  const bookingConversion = getConversionRate(appointments, leads)
  const visitConversion = getConversionRate(visits, appointments)
  const salesConversion = getConversionRate(sales, visits)

  // funnel widths from fact values
  const values = [leads, appointments, visits, sales]
  const widths = computeFunnelWidths(values, 50, 100)

  // Единый золотой цвет для всех этапов
  const baseColor = '#FFC107'
  
  // Функция для расчета цвета в зависимости от выполнения плана
  const getColorByPerformance = (fact: number, plan: number) => {
    if (!plan || plan === 0) return baseColor
    const performance = fact / plan
    
    // Если выполнение >= 100%, полная насыщенность
    // Если < 100%, уменьшаем насыщенность/затемняем
    if (performance >= 1) {
      return baseColor // #FFC107
    } else if (performance >= 0.8) {
      return '#FFD54F' // светлее при 80-100%
    } else if (performance >= 0.6) {
      return '#FFE082' // еще светлее при 60-80%
    } else if (performance >= 0.4) {
      return '#FFECB3' // совсем светлый при 40-60%
    } else {
      return '#FFF8E1' // почти белый при < 40%
    }
  }

  const colors = {
    leads: getColorByPerformance(leads, leadsplan),
    appointments: getColorByPerformance(appointments, appointmentsPlan),
    visits: getColorByPerformance(visits, visitsPlan),
    sales: getColorByPerformance(sales, salesPlan)
  }

  const filterOptions = [
    { value: 'current_period', label: 'Период целиком' },
    ...availableWeeks
  ]

  // Форматирование дат для отображения (используем теоретические границы)
  const formatDateRange = () => {
    if (!periodStartDate || !periodEndDate) return null
    
    const formatDate = (dateStr: string) => {
      const d = new Date(dateStr)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      return `${day}.${month}`
    }
    
    if (periodStartDate === periodEndDate) {
      return formatDate(periodStartDate)
    }
    
    return `${formatDate(periodStartDate)} — ${formatDate(periodEndDate)}`
  }

  const dateRange = formatDateRange()
  const planToDateRatio = React.useMemo(() => {
    if (!periodStartDate || !periodEndDate) return 1

    const rangeStart = new Date(`${periodStartDate}T00:00:00`)
    const rangeEnd = new Date(`${periodEndDate}T00:00:00`)
    const today = new Date(`${(todayDate || periodEndDate)}T00:00:00`)

    if (today <= rangeStart) return 0
    if (today >= rangeEnd) return 1

    const totalDays = Math.floor((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
    const elapsedDays = Math.floor((today.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1

    if (totalDays <= 0) return 1
    return Math.max(0, Math.min(1, elapsedDays / totalDays))
  }, [periodStartDate, periodEndDate, todayDate])

  const planToDate = React.useMemo(() => ({
    leads: Math.round(leadsplan * planToDateRatio),
    appointments: Math.round(appointmentsPlan * planToDateRatio),
    visits: Math.round(visitsPlan * planToDateRatio),
    sales: Math.round(salesPlan * planToDateRatio)
  }), [leadsplan, appointmentsPlan, visitsPlan, salesPlan, planToDateRatio])

  const availableFactorModes = React.useMemo(() => {
    const variants = factorAnalysisVariants
    if (!variants) return [] as Array<'mixed' | 'regular' | 'subscription'>
    return (['mixed', 'regular', 'subscription'] as const).filter((mode) => variants[mode] !== undefined)
  }, [factorAnalysisVariants])
  const hasFactorVariants = allowFactorSplitViews && availableFactorModes.length > 1
  const activeFactorAnalysisData = React.useMemo(() => {
    if (!factorAnalysisVariants) return factorAnalysisData
    if (factorAnalysisVariants[factorViewMode] !== undefined) {
      return factorAnalysisVariants[factorViewMode] || null
    }
    const fallbackMode = availableFactorModes[0]
    return fallbackMode ? factorAnalysisVariants[fallbackMode] || null : factorAnalysisData
  }, [factorAnalysisVariants, factorViewMode, availableFactorModes, factorAnalysisData])

  React.useEffect(() => {
    if (!factorAnalysisVariants) return
    if (factorAnalysisVariants[factorViewMode] !== undefined) return
    if (availableFactorModes.length > 0) {
      setFactorViewMode(availableFactorModes[0])
    }
  }, [factorAnalysisVariants, factorViewMode, availableFactorModes])

  const waterfallSteps = React.useMemo(
    () => (activeFactorAnalysisData ? buildWaterfallForMode(activeFactorAnalysisData, waterfallMode) : []),
    [activeFactorAnalysisData, waterfallMode]
  )
  const valueLevels = waterfallSteps.flatMap((step) => [step.start, step.end])
  const minLevel = valueLevels.length ? Math.min(...valueLevels) : 0
  const maxLevel = valueLevels.length ? Math.max(...valueLevels) : 1
  const levelRange = Math.max(1, maxLevel - minLevel)
  const axisMin = Math.max(0, minLevel - levelRange * 0.35)
  const axisMax = maxLevel + levelRange * 0.2
  const axisRange = Math.max(1, axisMax - axisMin)
  const hasWaterfall = waterfallSteps.length > 0
  const factorCards = (activeFactorAnalysisData?.factors || [])
    .slice()
    .sort((a, b) => (a.lostToDateAmount || 0) - (b.lostToDateAmount || 0))
    .slice(0, 5)
  const totalPotentialToPeriodEnd = factorCards.reduce(
    (sum, row) => sum + (row.expectedContributionByPlanCap || 0),
    0
  )
  const updatedForecastToPeriodEnd = (activeFactorAnalysisData?.forecastPeriodEndNewRevenue || 0) + totalPotentialToPeriodEnd
  const effectiveSubscriptionType: StudioSubscriptionType = hasFactorVariants
    ? factorViewMode === 'regular'
      ? 'REGULAR'
      : factorViewMode === 'subscription'
      ? 'SUBSCRIPTION'
      : 'MIXED'
    : subscriptionType
  const salesStageLabel = effectiveSubscriptionType === 'SUBSCRIPTION' ? 'Конверсия в подписку' : effectiveSubscriptionType === 'MIXED' ? 'Конверсия в продажу/подписку' : 'Конверсия в продажу'
  const salesStageVerb = effectiveSubscriptionType === 'SUBSCRIPTION' ? 'конверсию в подписку' : effectiveSubscriptionType === 'MIXED' ? 'конверсию в продажу/подписку' : 'конверсию в продажу'

  const factorLabel = (factor: FactorKey) => {
    if (factor === 'leads') return 'Заявки'
    if (factor === 'cr1') return 'Конверсия в запись'
    if (factor === 'cr2') return 'Доходимость'
    if (factor === 'cr3') return salesStageLabel
    return 'Средний чек'
  }

  const buildActionText = (factor: FactorKey, current: number, plan: number | null) => {
    if (plan === null) return 'Нет планового значения для сравнения'

    const diff = plan - current
    const over = current - plan

    if (factor === 'leads') {
      const diffPerDay = diff / 7
      const overPerDay = over / 7
      const planPerDay = plan / 7
      if (diff > 0) return `Подними заявки на +${diffPerDay.toFixed(1)} в день (до ${planPerDay.toFixed(1)} в день)`
      if (over > 0) return `Сохрани заявки не ниже ${planPerDay.toFixed(1)} в день`
      return `Держи заявки на уровне ${planPerDay.toFixed(1)} в день`
    }

    if (factor === 'aov') {
      if (diff > 0) return `Подними средний чек на +${formatCurrency(diff, currency)} (до ${formatCurrency(plan, currency)})`
      if (over > 0) return `Сохрани средний чек не ниже ${formatCurrency(plan, currency)}`
      return `Держи средний чек на уровне ${formatCurrency(plan, currency)}`
    }

    const diffPp = diff * 100
    const overPp = over * 100

    if (factor === 'cr1') {
      if (diffPp > 0) return `Подними конверсию в запись на +${diffPp.toFixed(1)} п.п. (до ${(plan * 100).toFixed(1)}%)`
      if (overPp > 0) return `Сохрани конверсию в запись не ниже ${(plan * 100).toFixed(1)}%`
      return `Держи конверсию в запись на уровне ${(plan * 100).toFixed(1)}%`
    }

    if (factor === 'cr2') {
      if (diffPp > 0) return `Подними доходимость на +${diffPp.toFixed(1)} п.п. (до ${(plan * 100).toFixed(1)}%)`
      if (overPp > 0) return `Сохрани доходимость не ниже ${(plan * 100).toFixed(1)}%`
      return `Держи доходимость на уровне ${(plan * 100).toFixed(1)}%`
    }

    if (diffPp > 0) return `Подними ${salesStageVerb} на +${diffPp.toFixed(1)} п.п. (до ${(plan * 100).toFixed(1)}%)`
    if (overPp > 0) return `Сохрани ${salesStageVerb} не ниже ${(plan * 100).toFixed(1)}%`
    return `Держи ${salesStageVerb} на уровне ${(plan * 100).toFixed(1)}%`
  }

  const formatValue = (factor: FactorKey, value: number | null) => {
    if (value === null) return '—'
    if (factor === 'aov') return `${formatCurrency(value, currency)}`
    if (factor === 'leads') return `${(value / 7).toFixed(1)} в день`
    return `${(value * 100).toFixed(1)}%`
  }

  const getDeviationData = (current: number, plan: number | null) => {
    if (plan === null || plan === 0) {
      return { pct: null as number | null, abs: null as number | null, tone: 'neutral' as const }
    }
    const pct = ((current - plan) / plan) * 100
    const abs = current - plan
    const tone: 'good' | 'warn' | 'bad' = pct >= -2 ? 'good' : pct >= -8 ? 'warn' : 'bad'
    return { pct, abs, tone }
  }

  const getToneClasses = (tone: 'good' | 'warn' | 'bad' | 'neutral') => {
    if (tone === 'good') return 'bg-green-50 border-green-200 text-green-700'
    if (tone === 'warn') return 'bg-amber-50 border-amber-200 text-amber-700'
    if (tone === 'bad') return 'bg-red-50 border-red-200 text-red-700'
    return 'bg-gray-50 border-gray-200 text-gray-600'
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[70%_30%] gap-4">
      <div className="space-y-4 min-w-0">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,0.75fr)] gap-4 items-stretch">
          {/* Блок воронки продаж */}
          <Card>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h3 className="text-base sm:text-lg font-medium">Воронка новых продаж</h3>

              {onFilterChange && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                  {dateRange && (
                    <span className="text-sm text-muted-foreground whitespace-nowrap">{dateRange}</span>
                  )}
                  <select
                    value={filterValue}
                    onChange={(e) => onFilterChange(e.target.value)}
                    className="w-full sm:w-auto px-3 py-1.5 text-sm border border-border rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {filterOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {!hasData ? (
              <div className="text-center py-8 text-muted-foreground">Недостаточно данных для отображения воронки продаж</div>
            ) : (
              <div style={{ padding: '4px 0 8px' }}>
                <FunnelRow
                idBase={`funnel-leads`}
                label="Заявки"
                fact={leads}
                plan={leadsplan}
                planToDate={planToDate.leads}
                showConversionHeader={true}
                topWidthPct={widths[0]}
                bottomWidthPct={widths[1]}
                  color={colors.leads}
                  daily={daily.leads}
                  trend={trend.leads}
                />

                <FunnelRow
                idBase={`funnel-appts`}
                label="Записи"
                fact={appointments}
                plan={appointmentsPlan}
                planToDate={planToDate.appointments}
                nextConversion={bookingConversion}
                nextConversionPlan={bookingConversionPlan}
                  topWidthPct={widths[1]}
                  bottomWidthPct={widths[2]}
                  color={colors.appointments}
                  daily={daily.appointments}
                  trend={trend.appointments}
                />

                <FunnelRow
                idBase={`funnel-visits`}
                label="Приходы"
                fact={visits}
                plan={visitsPlan}
                planToDate={planToDate.visits}
                nextConversion={visitConversion}
                nextConversionPlan={visitConversionPlan}
                  topWidthPct={widths[2]}
                  bottomWidthPct={widths[3]}
                  color={colors.visits}
                  daily={daily.visits}
                  trend={trend.visits}
                />

                <FunnelRow
                idBase={`funnel-sales`}
                label="Продажи"
                fact={sales}
                plan={salesPlan}
                planToDate={planToDate.sales}
                nextConversion={salesConversion}
                nextConversionPlan={salesConversionPlan}
                  topWidthPct={widths[3]}
                  bottomWidthPct={widths[3]}
                  color={colors.sales}
                  daily={daily.sales}
                  trend={trend.sales}
                />
              </div>
            )}
          </Card>

          <RecurringSalesDetailsCard
            subscriptionType={subscriptionType}
            details={recurringSalesDetails}
            mode={recurringDetailMode}
            onModeChange={setRecurringDetailMode}
            currency={currency}
          />
        </div>

        {hasWaterfall && (
          <Card>
                <h4 className="text-sm sm:text-base font-medium mb-2">Водопад влияния факторов на выручку по новым продажам за весь период</h4>
                <div className="flex flex-wrap gap-2 mb-3">
                  {(['plan_fact', 'plan_forecast', 'plan_growth'] as WaterfallMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setWaterfallMode(mode)}
                      className={`px-3 py-1.5 text-xs sm:text-sm rounded-md border transition-colors ${
                        waterfallMode === mode
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-card text-foreground border-border hover:bg-muted'
                      }`}
                    >
                      {WATERFALL_MODE_LABELS[mode]}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  {WATERFALL_MODE_DESCRIPTIONS[waterfallMode]}
                </p>

                <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
                  {waterfallSteps.map((step, index) => {
                    const lowValue = step.kind === 'total'
                      ? Math.max(axisMin, Math.min(step.start, step.end))
                      : Math.min(step.start, step.end)
                    const highValue = step.kind === 'total' ? Math.max(0, step.end) : Math.max(step.start, step.end)
                    const topPercent = ((axisMax - highValue) / axisRange) * 100
                    const heightPercent = Math.max(1.5, ((highValue - lowValue) / axisRange) * 100)
                    const prevStep = index > 0 ? waterfallSteps[index - 1] : null
                    const connectorTop = prevStep ? ((axisMax - prevStep.end) / axisRange) * 100 : 0
                    const isTotal = step.kind === 'total'
                    const toneClass = isTotal
                      ? step.key === 'end'
                        ? 'bg-emerald-500'
                        : 'bg-slate-500'
                      : step.value >= 0
                      ? 'bg-emerald-500'
                      : 'bg-rose-500'
                    const isEdgeColumn = index === 0 || index === waterfallSteps.length - 1
                    const showBreakMark = isTotal && !isEdgeColumn

                    return (
                      <div key={step.key} className="flex flex-col items-center min-w-0">
                        <div className="h-32 w-full relative">
                          {index > 0 && (
                            <div
                              className="absolute left-0 right-0 border-t border-dashed border-border/80"
                              style={{ top: `${connectorTop}%` }}
                            />
                          )}
                          <div
                            className={`absolute left-1/2 -translate-x-1/2 w-6 sm:w-7 rounded ${toneClass}`}
                            style={{
                              top: `${topPercent}%`,
                              height: `${heightPercent}%`
                            }}
                          />
                          {showBreakMark && (
                            <svg
                              className="absolute z-20 left-1/2 -translate-x-1/2 w-6 sm:w-7 h-3"
                              style={{ bottom: '6px' }}
                              viewBox="0 0 28 12"
                              aria-hidden="true"
                            >
                              <path
                                d="M1 4 L5 8 L9 4 L13 8 L17 4 L21 8 L25 4"
                                fill="none"
                                stroke="rgba(255,255,255,0.98)"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M1 1 L5 5 L9 1 L13 5 L17 1 L21 5 L25 1"
                                fill="none"
                                stroke="rgba(255,255,255,0.98)"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="text-[10px] sm:text-[11px] text-center mt-1 leading-tight min-h-[30px]">
                          {step.label}
                        </div>
                        <div className={`text-[10px] sm:text-[11px] font-medium tabular-nums ${
                          isTotal ? 'text-slate-700' : step.value >= 0 ? 'text-emerald-700' : 'text-rose-700'
                        }`}>
                          {isTotal ? `${formatCurrency(step.end, currency)}` : `${step.value > 0 ? '+' : ''}${formatCurrency(step.value, currency)}`}
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3">
                  <button
                    type="button"
                    onClick={() => setIsWaterfallGuideOpen((prev) => !prev)}
                    className="w-full flex items-center justify-between text-left text-xs sm:text-sm font-semibold text-foreground"
                    aria-expanded={isWaterfallGuideOpen}
                  >
                    <span>Инструкция по водопадам</span>
                    <span className="text-muted-foreground">{isWaterfallGuideOpen ? 'Скрыть' : 'Показать'}</span>
                  </button>
                  {isWaterfallGuideOpen && (
                    <div className="mt-2 space-y-2 text-xs sm:text-sm">
                      <p>
                        <span className="font-medium">1) План - Факт:</span>{' '}
                        {WATERFALL_MODE_INSTRUCTIONS.plan_fact.useCase} {WATERFALL_MODE_INSTRUCTIONS.plan_fact.reading}
                      </p>
                      <p>
                        <span className="font-medium">2) План - Текущий прогноз:</span>{' '}
                        {WATERFALL_MODE_INSTRUCTIONS.plan_forecast.useCase} {WATERFALL_MODE_INSTRUCTIONS.plan_forecast.reading}
                      </p>
                      <p>
                        <span className="font-medium">3) План - Потенциал роста:</span>{' '}
                        {WATERFALL_MODE_INSTRUCTIONS.plan_growth.useCase} {WATERFALL_MODE_INSTRUCTIONS.plan_growth.reading}
                      </p>
                      <p className="text-muted-foreground">
                        Связь диаграммы и блока факторного анализа: конечная колонка <span className="font-medium">"Прогноз"</span> в режиме
                        <span className="font-medium"> "План - Текущий прогноз"</span> = первое число в строке
                        <span className="font-medium"> "Текущий прогноз и план до конца периода"</span>. Колонка
                        <span className="font-medium"> "План"</span> = второе число в этой же строке. Конечная колонка
                        <span className="font-medium"> "Рост"</span> в режиме <span className="font-medium">"План - Потенциал роста"</span> =
                        итог в строке <span className="font-medium">"Общий потенциал до конца периода"</span>, а число в скобках рядом с плюсом
                        показывает добавку к прогнозу. Шаги факторов на водопаде соответствуют строке
                        <span className="font-medium"> "Недополучено / Сверх плана"</span> в карточках тех же факторов; поле
                        <span className="font-medium"> "Потенциал до конца периода"</span> в карточке показывает резерв по каждому шагу.
                        Приоритет работы задается порядком карточек сверху вниз.
                      </p>
                    </div>
                  )}
                </div>
          </Card>
        )}
      </div>

      {/* Блок факторного анализа потерь */}
      <Card>
        <div className="mb-4">
          <h3 className="text-base sm:text-lg font-medium">💡 Что нужно делать в первую очередь</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Где ты теряешь больше всего и на что нужно обратить внимание в первую очередь
          </p>
          {hasFactorVariants && (
            <div className="mt-3 flex flex-wrap gap-2">
              {([
                { key: 'mixed', label: 'Общий' },
                { key: 'regular', label: 'Абонементы' },
                { key: 'subscription', label: 'Подписки' }
              ] as const)
                .filter((item) => availableFactorModes.includes(item.key))
                .map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setFactorViewMode(item.key)}
                    className={`px-3 py-1.5 text-xs sm:text-sm rounded-md border transition-colors ${
                      factorViewMode === item.key
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-foreground border-border hover:bg-muted'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
            </div>
          )}
          {!!activeFactorAnalysisData && factorCards.length > 0 && (
            <div className="mt-3 text-sm space-y-1">
              <p>
                Текущий прогноз и план до конца периода:{' '}
                <span
                  className={`font-bold ${
                    activeFactorAnalysisData.forecastPeriodEndNewRevenue >= activeFactorAnalysisData.planPeriodNewRevenue
                      ? 'text-green-700'
                      : 'text-red-700'
                  }`}
                >
                  {formatCurrency(activeFactorAnalysisData.forecastPeriodEndNewRevenue, currency)}
                </span>{' '}
                /{' '}
                <span className="font-bold text-gray-800">{formatCurrency(activeFactorAnalysisData.planPeriodNewRevenue, currency)}</span>
              </p>
              <p>
                Общий потенциал до конца периода:{' '}
                <span className="font-bold text-gray-800">{formatCurrency(updatedForecastToPeriodEnd, currency)}</span>{' '}
                <span className="font-bold text-green-700">(+{formatCurrency(totalPotentialToPeriodEnd, currency)})</span>
              </p>
            </div>
          )}
        </div>
        {!activeFactorAnalysisData || factorCards.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="font-medium">Недостаточно данных для анализа</p>
            <p className="text-sm mt-2">
              Нам нужно больше данных, чтобы выполнить анализ потерь и дать рекомендации по ключевым действиям воронки
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="rounded-lg border border-border bg-muted/20 p-3">
              <button
                type="button"
                onClick={() => setIsFactorGuideOpen((prev) => !prev)}
                className="w-full flex items-center justify-between text-left text-xs sm:text-sm font-semibold text-foreground"
                aria-expanded={isFactorGuideOpen}
              >
                <span>Инструкция по факторному блоку</span>
                <span className="text-muted-foreground">{isFactorGuideOpen ? 'Скрыть' : 'Показать'}</span>
              </button>
              {isFactorGuideOpen && (
                <div className="mt-2 space-y-1.5 text-xs sm:text-sm">
                  <p>
                    <span className="font-medium">Текущий прогноз / План до конца периода:</span> ожидаемая выручка при текущем темпе против цели периода. 
                    Прогноз построен исходя из ваших средних показателей за последние 10 дней. 
                  </p>
                  <p>
                    <span className="font-medium">Общий потенциал до конца периода:</span> на какую выручку можно выйти и сколько можно добрать от текущего результата, если дотянуть отстающие факторы до плана.
                  </p>
                  <p>
                    <span className="font-medium">Приоритет работы с факторами:</span> карточки отсортированы по наибольшему влиянию на выручку, поэтому действия
                    нужно ставить в приоритет по порядку списка.
                  </p>
                  <p>
                    <span className="font-medium">Недополучено / Сверх плана:</span> вклад конкретного фактора в разрыв по выручке с начала периода до сегодняшнего дня.
                  </p>
                  <p>
                    <span className="font-medium">Потенциал до конца периода (в карточке):</span> денежный эффект, который этот фактор еще может дать в этом периоде, если с завтрашнего дня поднять метрику до планового значения.
                  </p>
                  <p>
                    <span className="font-medium">Потенциал роста X -&gt; Y:</span> на сколько нужно подтянуть саму метрику (заявки/конверсию/чек) до планового уровня.
                  </p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-[1fr_auto] items-center px-2 text-[10px] sm:text-xs text-muted-foreground font-medium">
              <span>Фактор</span>
              <span className="text-right">Влияние на выручку на сегодня</span>
            </div>
            {factorCards.map((row, idx) => {
              const deviation = getDeviationData(row.currentValue, row.planValue)
              const actionText = buildActionText(row.factor, row.currentValue, row.planValue)

              return (
                <div key={row.factor} className="border rounded-lg p-2.5 bg-card">
                  <div className={`text-xs border rounded-md px-2 py-1.5 inline-flex items-center gap-2 ${getToneClasses(deviation.tone)}`}>
                    <span className="font-semibold">{idx + 1}.</span>
                    <span className="font-semibold">{actionText}</span>
                  </div>

                  <div className="mt-2 flex items-start justify-between gap-2">
                    <p className="font-semibold text-sm leading-tight">{factorLabel(row.factor)}</p>
                    <p
                      className={`text-sm font-extrabold whitespace-nowrap leading-none ${
                        row.lostToDateAmount === null
                          ? 'text-gray-600'
                          : row.lostToDateAmount > 0
                          ? 'text-green-700'
                          : row.lostToDateAmount < 0
                          ? 'text-red-700'
                          : 'text-gray-600'
                      }`}
                    >
                      {row.lostToDateAmount === null
                        ? 'Нет данных'
                        : row.lostToDateAmount < 0
                        ? `Недополучено: ${formatCurrency(Math.abs(row.lostToDateAmount), currency)}`
                        : `Сверх плана: ${formatCurrency(row.lostToDateAmount, currency)}`}
                    </p>
                  </div>

                  <div className="mt-1 text-xs">
                    <span className="text-muted-foreground">
                      {'Потенциал до конца периода:'}
                    </span>{' '}
                    <span className="font-semibold text-green-700">+{formatCurrency(row.expectedContributionByPlanCap, currency)}</span>
                  </div>

                  <div className="mt-1.5 text-xs">
                    {row.planValue !== null && row.currentValue > row.planValue ? (
                      <>
                        <span className="text-muted-foreground">Удержание на уровне факта:</span>{' '}
                        <span className="font-medium">{formatValue(row.factor, row.currentValue)}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-muted-foreground">Потенциал роста:</span>{' '}
                        <span className="font-medium">
                          {formatValue(row.factor, row.currentValue)} {'->'} {formatValue(row.factor, row.planValue)}
                        </span>
                      </>
                    )}
                  </div>

                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}
